'use client';

import { isValidDateFormat, pastDateValidator } from '@florencelabs/date-field';
import { useEffect, ReactElement } from 'react';
import { type FieldErrors, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { AppointmentSummary } from '@provider-directory/components/appointment-summary/appointment-summary.component';
import {
  ScheduleForm,
  FormConfig,
  FormBlockType,
} from '@provider-directory/components/forms';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { SceneFormWithNavigation } from '@provider-directory/components/scene-form/scene-form.component';
import { SplitScreen } from '@provider-directory/components/split-screen/split-screen.component';
import {
  VALID_EMAIL_REGEX,
  US_STATES,
  NAME_REGEX,
} from '@provider-directory/constants';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { ClientUIConfig } from '@/types';

export const PATIENT_CONTACT_PHONE_INPUT_MASK = '(999) 999-9999';
export const PATIENT_CONTACT_PHONE_VALIDATION_PATTERN =
  /^\(\d{3}\) \d{3}-\d{4}$/;

export const patientInfoValues = z.object({
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  dob: z.string().optional(),
  legalSex: z.string().optional(),
  socialSecurityNumber: z.string().optional(),
  email: z.string().optional(),
  phoneNumber: z.string().optional(),
  phoneType: z.enum(['home', 'work', 'mobile', 'other']).optional(),
  smsOptIn: z.boolean().optional(),
  streetAddress: z.string().optional(),
  apartment: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  comments: z.string().optional(),
});
export type PatientInfoValues = z.infer<typeof patientInfoValues>;

const buildPatientInfoConfig = (
  orgConfig: ClientUIConfig,
): FormConfig<PatientInfoValues> => [
  {
    blockId: 'patientInfo',
    title: 'schedule.patientInfo.title',
    type: FormBlockType.SECTION,
    children: [
      {
        type: FormBlockType.ROW,
        blockId: 'patientInfoNameRow',
        children: [
          {
            name: 'firstName',
            label: 'schedule.patientInfo.firstName.label',
            type: FormBlockType.TEXT,
            validation: {
              required: 'schedule.patientInfo.firstName.required',
              pattern: {
                value: NAME_REGEX,
                message: 'schedule.patientInfo.validCharacters',
              },
              maxLength: 80,
            },
          },
          {
            name: 'middleName',
            label: 'schedule.patientInfo.middleName.label',
            type: FormBlockType.TEXT,
            validation: {
              pattern: {
                value: NAME_REGEX,
                message: 'schedule.patientInfo.validCharacters',
              },
              maxLength: 80,
            },
          },
        ],
      },
      {
        name: 'lastName',
        label: 'schedule.patientInfo.lastName.label',
        type: FormBlockType.TEXT,
        validation: {
          required: 'schedule.patientInfo.lastName.required',
          pattern: {
            value: NAME_REGEX,
            message: 'schedule.patientInfo.validCharacters',
          },
          maxLength: 80,
        },
      },
      {
        name: 'dob',
        label: 'schedule.patientInfo.dob.label',
        type: FormBlockType.DATE,
        validation: {
          required: 'schedule.patientInfo.dob.required',
        },
        validations: [
          isValidDateFormat('schedule.patientInfo.dob.invalidFormat'),
          pastDateValidator('schedule.patientInfo.dob.invalidFormat'),
        ],
      },
      {
        name: 'legalSex',
        label: 'schedule.patientInfo.legalSex.title',
        type: FormBlockType.SELECT,
        validation: {
          required: 'schedule.patientInfo.legalSex.required',
        },
        tooltip: 'schedule.patientInfo.legalSex.tooltip',
        tooltipVariant: 'dark',
        options: [
          { value: 'male', label: 'schedule.patientInfo.legalSex.male' },
          { value: 'female', label: 'schedule.patientInfo.legalSex.female' },
          {
            value: 'nonBinary',
            label: 'schedule.patientInfo.legalSex.nonBinary',
          },
          { value: 'other', label: 'schedule.patientInfo.legalSex.other' },
        ],
      },
      {
        name: 'socialSecurityNumber',
        label: 'schedule.patientInfo.socialSecurityNumber.label',
        mask: {
          pattern: '999-99-9999',
        },
        inputType: 'tel',
        type: FormBlockType.TEXT,
        validation: {
          pattern: {
            value: /^\d{3}-\d{2}-\d{4}$/,
            message: 'schedule.patientInfo.socialSecurityNumber.invalid',
          },
        },
      },
    ],
  },
  {
    blockId: 'contactInfo',
    title: 'schedule.contactInfo.title',
    type: FormBlockType.SECTION,
    children: [
      {
        name: 'email',
        label: 'schedule.contactInfo.email.label',
        type: FormBlockType.TEXT,
        validation: {
          required: 'schedule.contactInfo.email.required',
          maxLength: 255,
        },
        validations: [
          (value: string) => {
            return (
              VALID_EMAIL_REGEX.test(value) ||
              'schedule.contactInfo.email.invalid'
            );
          },
        ],
      },
      {
        type: FormBlockType.ROW,
        blockId: 'contactInfoPhoneRow',
        children: [
          {
            name: 'phoneNumber',
            label: 'schedule.contactInfo.phoneNumber.label',
            mask: {
              pattern: PATIENT_CONTACT_PHONE_INPUT_MASK,
            },
            type: FormBlockType.TEXT,
            validation: {
              required: 'schedule.contactInfo.phoneNumber.required',
              pattern: {
                value: PATIENT_CONTACT_PHONE_VALIDATION_PATTERN,
                message: 'schedule.contactInfo.phoneNumber.invalid',
              },
            },
          },
          {
            name: 'phoneType',
            label: 'schedule.contactInfo.phoneType.label',
            type: FormBlockType.SELECT,
            validation: {
              required: 'schedule.contactInfo.phoneType.required',
            },
            options: [
              {
                value: 'mobile',
                label: 'schedule.contactInfo.phoneType.mobile',
              },
              { value: 'home', label: 'schedule.contactInfo.phoneType.home' },
              { value: 'work', label: 'schedule.contactInfo.phoneType.work' },
            ],
          },
        ],
      },
      {
        blockId: 'smsOptInSection',
        type: FormBlockType.SECTION,
        variant: 'secondary',
        isHiddenValidation: ({ phoneNumber, phoneType }) =>
          !phoneNumber || phoneType !== 'mobile',
        children: [
          {
            name: 'smsOptIn',
            label: 'schedule.contactInfo.smsOptIn.label',
            type: FormBlockType.CHECKBOX,
            ariaDescribedBy: 'smsOptInDescription smsOptInLinks',
          },
          {
            blockId: 'smsOptInDescription',
            content: 'schedule.contactInfo.smsOptIn.description',
            type: FormBlockType.TEXT_CONTENT,
          },
          {
            blockId: 'smsOptInLinks',
            content: [
              {
                text: 'schedule.contactInfo.smsOptIn.smsTerms',
                href: orgConfig.legal?.smsTermsUrl,
                target: '_blank',
              },
              {
                text: 'fabricFooter.privacyPolicy',
                href: orgConfig.legal?.privacyPolicyUrl,
                target: '_blank',
              },
            ],
            separator: '•',
            type: FormBlockType.TEXT_CONTENT,
          },
        ],
      },
      {
        name: 'streetAddress',
        label: 'schedule.contactInfo.streetAddress',
        type: FormBlockType.TEXT,
      },
      {
        name: 'apartment',
        label: 'schedule.contactInfo.apartment',
        type: FormBlockType.TEXT,
      },
      {
        type: FormBlockType.ROW,
        blockId: 'contactInfoRowCityState',
        children: [
          {
            name: 'city',
            label: 'schedule.contactInfo.city',
            type: FormBlockType.TEXT,
          },
          {
            name: 'state',
            label: 'schedule.contactInfo.state',
            type: FormBlockType.COMBOBOX,
            options: US_STATES.map((state) => ({ value: state, label: state })),
          },
        ],
      },
      {
        name: 'zip',
        label: 'schedule.contactInfo.zip',
        type: FormBlockType.TEXT,
      },
    ],
  },
  {
    type: FormBlockType.SECTION,
    blockId: 'commentsSection',
    children: [
      {
        name: 'comments',
        label: 'schedule.comments.title',
        type: FormBlockType.TEXTAREA,
        validation: {
          required: 'schedule.comments.required',
          maxLength: 250,
        },
        placeholder: 'schedule.comments.placeholder',
      },
    ],
  },
];

const fieldOrder: (keyof PatientInfoValues)[] = [
  'firstName',
  'middleName',
  'lastName',
  'dob',
  'legalSex',
  'socialSecurityNumber',
  'email',
  'phoneNumber',
  'phoneType',
  'smsOptIn',
  'streetAddress',
  'apartment',
  'city',
  'state',
  'zip',
  'comments',
] as const;

export default function PatientInfo({
  onPrev,
  onNext,
}: {
  onPrev: () => void;
  onNext: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { appointment, updateAppointment } = useAppointment();
  const { provider } = useProvider();
  const analytics = useAnalytics();
  const { config } = useOrgContext();
  const patientInfoConfig = buildPatientInfoConfig(config);
  const { handleSubmit, setValue, control, setFocus, watch } =
    useForm<PatientInfoValues>({
      mode: 'onTouched',
      shouldFocusError: false,
    });
  const onSubmit: SubmitHandler<PatientInfoValues> = (data) => {
    analytics.completedFormEvent({
      formStep: 'PatientInfo',
      linkContextPage: 'SchedulingPatientInfo',
      provider: provider ?? undefined,
    });
    updateAppointment(data);
    onNext();
  };

  const onError = (formErrors: FieldErrors<PatientInfoValues>) => {
    const firstErrorField = fieldOrder.find((name) => !!formErrors[name]);

    const nonTextInputFields: (keyof Partial<PatientInfoValues>)[] = [
      'legalSex',
      'phoneType',
      'state',
    ];

    if (firstErrorField) {
      const element = document.querySelector(`[name="${firstErrorField}"]`);

      if (element) {
        element.scrollIntoView({ block: 'center' });
      }

      if (!nonTextInputFields.includes(firstErrorField)) {
        setFocus(firstErrorField);
      }
    }
  };

  useEffect(() => {
    const formFields: PatientInfoValues = {
      firstName: appointment.firstName || undefined,
      middleName: appointment.middleName || undefined,
      lastName: appointment.lastName || undefined,
      dob: appointment.dob || undefined,
      legalSex: appointment.legalSex || undefined,
      socialSecurityNumber: appointment.socialSecurityNumber || undefined,
      email: appointment.email || undefined,
      phoneNumber: appointment.phoneNumber || undefined,
      phoneType: appointment.phoneType ?? undefined,
      smsOptIn: appointment.smsOptIn,
      streetAddress: appointment.streetAddress || undefined,
      apartment: appointment.apartment || undefined,
      city: appointment.city || undefined,
      state: appointment.state || undefined,
      zip: appointment.zip || undefined,
      comments: appointment.comments || undefined,
    };
    Object.entries(formFields).forEach(([key, value]): void => {
      setValue(key as keyof PatientInfoValues, value);
    });
  }, [appointment, setValue]);

  return (
    <SplitScreen
      additionalClassnames={{
        left: {
          screen: 'bg-white',
        },
        right: {
          screen: 'bg-white desktop:bg-primary-50',
        },
      }}
    >
      <SceneFormWithNavigation
        onSubmit={handleSubmit(onSubmit, onError)}
        onPrev={onPrev}
        formId={'patient-info-form'}
      >
        <h1
          className={
            'font-serif text-display-large desktop:text-display-xlarge'
          }
        >
          {t('schedule.scheduleAnAppointment')}
        </h1>
        <ScheduleForm<PatientInfoValues>
          blocks={patientInfoConfig}
          control={control}
          watch={watch}
        />
      </SceneFormWithNavigation>
      <AppointmentSummary />
    </SplitScreen>
  );
}
